import { useEffect, useState } from "react";
import { Volume2, Monitor, Gamepad2, UserRound, Shirt, RotateCcw, Keyboard, SlidersHorizontal, Save, Trash2, LogIn, PersonStanding, Palette, Check, Globe, FlaskConical } from "lucide-react";
import { allThemes } from "./menuThemes";
import { useI18n } from "@/i18n";
import { listInstalled, activePack, setActivePack, isPackEnabled, setPackEnabled, PACK_SLOTS } from "@/components/cef/packs";
import { toast } from "sonner";
import api from "@/lib/api";
import { TesterLinkPanel } from "@/components/menu/TesterLinkPanel";
import { IS_INGAME } from "@/components/cef/cefShared";

const STORED_GFX = "nc_menu_gfx";
const STORED_GAMEPLAY = "nc_menu_gameplay";
const STORED_BINDS = "nc_menu_binds";
const STORED_LOADOUTS = "nc_menu_loadouts";

const load = (k, fb) => { try { return JSON.parse(localStorage.getItem(k)) ?? fb; } catch { return fb; } };

/* ---------------- SETTINGS (advanced) ---------------- */

const DEFAULT_GFX = { shadows: "Epic", dlss: true, fps: 144, resScale: 100, textures: "High", viewDistance: "High", aa: "TAA", vsync: true, motionBlur: false, foliage: "High" };
const DEFAULT_GAMEPLAY = { subtitles: true, crosshair: "Dynamic", minimap: true, damageFeedback: true, streamerMode: false, camShake: 60, uiScale: 100, killfeed: true };
const DEFAULT_BINDS = { "Move Forward": "W", "Move Back": "S", "Strafe Left": "A", "Strafe Right": "D", "Sprint": "Shift", "Interact": "E", "Phone": "P", "Radio": "CapsLock", "Inventory": "Tab", "Pause Menu": "Escape" };
const BIND_LABELS = { "Move Forward": "bind.forward", "Move Back": "bind.back", "Strafe Left": "bind.left", "Strafe Right": "bind.right", "Sprint": "bind.sprint", "Interact": "bind.interact", "Phone": "bind.phone", "Radio": "bind.radio", "Inventory": "bind.inventory", "Pause Menu": "bind.pause" };

export function SettingsPanel({ audio, onBack, accent = "#2E6BFF", theme, onTheme, onPacksChange }) {
  const { t, lang, setLang, languages } = useI18n();
  const [tab, setTab] = useState("graphics");
  const [gfx, setGfx] = useState(() => ({ ...DEFAULT_GFX, ...load(STORED_GFX, {}) }));
  const [gp, setGp] = useState(() => ({ ...DEFAULT_GAMEPLAY, ...load(STORED_GAMEPLAY, {}) }));
  const [binds, setBinds] = useState(() => ({ ...DEFAULT_BINDS, ...load(STORED_BINDS, {}) }));
  const [dirty, setDirty] = useState(false);
  const [vol, setVol] = useState({ ...audio.volRef.current });
  const [listening, setListening] = useState(null); // action awaiting a key

  const setG = (k, v) => { setGfx((p) => ({ ...p, [k]: v })); setDirty(true); audio.tick(); };
  const setP = (k, v) => { setGp((p) => ({ ...p, [k]: v })); setDirty(true); audio.tick(); };
  const apply = () => {
    localStorage.setItem(STORED_GFX, JSON.stringify(gfx));
    localStorage.setItem(STORED_GAMEPLAY, JSON.stringify(gp));
    setDirty(false); audio.click(); toast.success("Settings applied — the game client mirrors these into NCUser.ini");
  };
  const setVolume = (k, v) => { const next = { ...vol, [k]: v }; setVol(next); audio.setVolumes(next); };

  // keybind capture
  useEffect(() => {
    if (!listening) return;
    const cap = (e) => {
      e.preventDefault();
      const key = e.key === " " ? "Space" : e.key;
      const conflict = Object.entries(binds).find(([a, k]) => k.toLowerCase() === key.toLowerCase() && a !== listening);
      if (conflict) { audio.deny(); toast.error(`${key} is already bound to ${conflict[0]}`); setListening(null); return; }
      const next = { ...binds, [listening]: key };
      setBinds(next);
      localStorage.setItem(STORED_BINDS, JSON.stringify(next));
      audio.click();
      setListening(null);
    };
    window.addEventListener("keydown", cap, { once: true });
    return () => window.removeEventListener("keydown", cap);
  }, [listening, binds, audio]);

  const Row = ({ label, children, hint }) => (
    <div className="flex items-center justify-between gap-6 py-3 border-b border-white/5">
      <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-300">{label}{hint && <span className="block text-[9px] text-zinc-500 normal-case tracking-normal mt-0.5">{hint}</span>}</span>
      {children}
    </div>
  );
  const Seg = ({ options, value, onChange, testid }) => (
    <div className="flex border border-white/15 flex-wrap">
      {options.map((o) => (
        <button key={String(o)} data-testid={`${testid}-${o}`} onClick={() => onChange(o)}
          className={`px-3 py-1.5 font-mono text-[10px] tracking-[0.1em] uppercase transition-colors ${value === o ? "text-black" : "text-zinc-300 hover:text-zinc-100"}`}
          style={value === o ? { backgroundColor: accent } : {}}>
          {String(o)}
        </button>
      ))}
    </div>
  );
  const Toggle = ({ value, onChange, testid }) => <Seg testid={testid} options={["Off", "On"]} value={value ? "On" : "Off"} onChange={(v) => onChange(v === "On")} />;
  const Slider = ({ value, min, max, step = 1, onChange, testid, suffix = "" }) => (
    <span className="flex items-center gap-3">
      <input data-testid={testid} type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-44 accent-[#2E6BFF]" />
      <span className="font-mono text-[11px] text-zinc-200 w-14 text-right">{value}{suffix}</span>
    </span>
  );

  const TABS = [["graphics", Monitor, t("settings.graphics")], ["audio", Volume2, t("settings.audio")], ["controls", Keyboard, t("settings.controls")], ["gameplay", SlidersHorizontal, t("settings.gameplay")], ["theme", Palette, t("settings.theme")], ["language", Globe, t("settings.language")], ["testing", FlaskConical, "Testing"]];

  return (
    <div data-testid="settings-panel" className="absolute inset-0 flex flex-col px-6 sm:px-10 pt-20 pb-6" style={{ animation: "ncFadeIn 0.3s ease both" }}>
      <div className="flex items-center gap-3 flex-wrap">
        <button data-testid="settings-back" onClick={onBack} className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-400 hover:text-zinc-100 transition-colors mr-2">‹ {t("settings.back")}</button>
        <h2 className="font-semibold uppercase tracking-[0.2em] text-lg sm:text-xl text-zinc-50">{t("nav.settings")}</h2>
        <div className="ml-auto flex gap-1 flex-wrap">
          {TABS.map(([id, Icon, label]) => (
            <button key={id} data-testid={`settings-tab-${id}`} onClick={() => { setTab(id); audio.tick(); }}
              className={`px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase border transition-colors flex items-center gap-1.5 ${tab === id ? "text-black" : "text-zinc-300 border-white/15 hover:border-white/40"}`}
              style={tab === id ? { backgroundColor: accent, borderColor: accent } : {}}>
              <Icon className="w-3 h-3" /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 w-full max-w-2xl border border-white/10 bg-[#080a0f]/85 backdrop-blur-md p-6 overflow-y-auto min-h-0" style={{ scrollbarWidth: "thin" }}>
        {tab === "graphics" && (
          <div data-testid="settings-graphics">
            <Row label={t("gfx.shadows")}><Seg testid="gfx-shadows" options={["Low", "Medium", "High", "Epic"]} value={gfx.shadows} onChange={(v) => setG("shadows", v)} /></Row>
            <Row label={t("gfx.textures")}><Seg testid="gfx-textures" options={["Low", "Medium", "High", "Epic"]} value={gfx.textures} onChange={(v) => setG("textures", v)} /></Row>
            <Row label={t("gfx.distance")}><Seg testid="gfx-distance" options={["Near", "Medium", "High", "Epic"]} value={gfx.viewDistance} onChange={(v) => setG("viewDistance", v)} /></Row>
            <Row label={t("gfx.foliage")}><Seg testid="gfx-foliage" options={["Low", "Medium", "High"]} value={gfx.foliage} onChange={(v) => setG("foliage", v)} /></Row>
            <Row label={t("gfx.aa")}><Seg testid="gfx-aa" options={["Off", "FXAA", "TAA"]} value={gfx.aa} onChange={(v) => setG("aa", v)} /></Row>
            <Row label="DLSS" hint="NVIDIA upscaling"><Toggle testid="gfx-dlss" value={gfx.dlss} onChange={(v) => setG("dlss", v)} /></Row>
            <Row label="VSync"><Toggle testid="gfx-vsync" value={gfx.vsync} onChange={(v) => setG("vsync", v)} /></Row>
            <Row label={t("gfx.motionblur")}><Toggle testid="gfx-motionblur" value={gfx.motionBlur} onChange={(v) => setG("motionBlur", v)} /></Row>
            <Row label={t("gfx.resscale")}><Slider testid="gfx-resscale" value={gfx.resScale} min={50} max={150} step={5} suffix="%" onChange={(v) => setG("resScale", v)} /></Row>
            <Row label={t("gfx.fps")}><Seg testid="gfx-fps" options={[60, 120, 144, 240]} value={gfx.fps} onChange={(v) => setG("fps", v)} /></Row>
          </div>
        )}
        {tab === "audio" && (
          <div data-testid="settings-audio">
            {[["master", t("audio.master")], ["music", t("audio.music")], ["sfx", t("audio.sfx")]].map(([k, label]) => (
              <Row key={k} label={label}>
                <span className="flex items-center gap-3">
                  <input data-testid={`vol-${k}`} type="range" min="0" max="1" step="0.01" value={vol[k]}
                    onChange={(e) => setVolume(k, Number(e.target.value))} onMouseUp={() => audio.tick()} onTouchEnd={() => audio.tick()}
                    className="w-44 accent-[#2E6BFF]" />
                  <span className="font-mono text-[11px] text-zinc-200 w-10 text-right">{Math.round(vol[k] * 100)}%</span>
                </span>
              </Row>
            ))}
            <Row label={t("settings.test_sound")}><button data-testid="audio-test" onClick={() => audio.click()} className="border border-white/20 hover:border-white/50 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors">{t("settings.play")}</button></Row>
            <p className="font-mono text-[10px] text-zinc-400 tracking-[0.1em] uppercase mt-4">live — sliders drive this menu's synth buses right now</p>
          </div>
        )}
        {tab === "controls" && (
          <div data-testid="settings-controls">
            <p className="font-mono text-[10px] text-zinc-400 uppercase tracking-[0.1em] mb-2">Click a binding, then press the new key. Conflicts are blocked.</p>
            {Object.entries(binds).map(([action, key]) => (
              <Row key={action} label={t(BIND_LABELS[action] || action)}>
                <button data-testid={`bind-${action.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => { setListening(action); audio.tick(); }}
                  className={`min-w-28 px-3 py-1.5 border font-mono text-[11px] uppercase tracking-[0.1em] transition-colors ${listening === action ? "text-black animate-pulse" : "text-zinc-200 border-white/15 hover:border-white/50"}`}
                  style={listening === action ? { backgroundColor: accent, borderColor: accent } : {}}>
                  {listening === action ? t("settings.press_key") : key}
                </button>
              </Row>
            ))}
            <button data-testid="binds-reset" onClick={() => { setBinds(DEFAULT_BINDS); localStorage.removeItem(STORED_BINDS); audio.back(); toast.success("Bindings reset to defaults"); }}
              className="mt-4 flex items-center gap-2 border border-white/15 hover:border-white/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300 transition-colors">
              <RotateCcw className="w-3.5 h-3.5" /> {t("settings.reset")}
            </button>
          </div>
        )}
        {tab === "gameplay" && (
          <div data-testid="settings-gameplay">
            <Row label={t("gp.subtitles")}><Toggle testid="gp-subtitles" value={gp.subtitles} onChange={(v) => setP("subtitles", v)} /></Row>
            <Row label={t("gp.crosshair")}><Seg testid="gp-crosshair" options={["Off", "Simple", "Dynamic"]} value={gp.crosshair} onChange={(v) => setP("crosshair", v)} /></Row>
            <Row label={t("gp.minimap")}><Toggle testid="gp-minimap" value={gp.minimap} onChange={(v) => setP("minimap", v)} /></Row>
            <Row label={t("gp.killfeed")}><Toggle testid="gp-killfeed" value={gp.killfeed} onChange={(v) => setP("killfeed", v)} /></Row>
            <Row label={t("gp.damage")}><Toggle testid="gp-damage" value={gp.damageFeedback} onChange={(v) => setP("damageFeedback", v)} /></Row>
            <Row label={t("gp.streamer")} hint="hides player names in HUD"><Toggle testid="gp-streamer" value={gp.streamerMode} onChange={(v) => setP("streamerMode", v)} /></Row>
            <Row label={t("gp.shake")}><Slider testid="gp-shake" value={gp.camShake} min={0} max={100} step={5} suffix="%" onChange={(v) => setP("camShake", v)} /></Row>
            <Row label={t("gp.uiscale")}><Slider testid="gp-uiscale" value={gp.uiScale} min={75} max={150} step={5} suffix="%" onChange={(v) => setP("uiScale", v)} /></Row>
          </div>
        )}
        {tab === "theme" && (
          <div data-testid="settings-theme" className="flex flex-col gap-2.5">
            <p className="font-mono text-[9px] text-zinc-500 tracking-[0.15em] uppercase leading-relaxed">
              Re-skin the boot cinematic and main menu. More themes live on the mod workshop (Config Pack → menu theme) — installed packs appear here.
            </p>
            {allThemes().map((t) => {
              const active = theme?.id === t.id;
              return (
                <button key={t.id} data-testid={`theme-${t.id.replace(/[^a-z0-9]/gi, "-")}`}
                  onClick={() => { onTheme?.(t.id); audio.click(); toast.success(`Menu theme: ${t.name}`); }}
                  className={`text-left border p-4 flex items-center gap-4 transition-colors ${active ? "border-white/60 bg-white/5" : "border-white/10 hover:border-white/30"}`}>
                  <span className="w-8 h-8 shrink-0 rounded-full border-2" style={{ backgroundColor: t.accent, borderColor: active ? "#fff" : "transparent", boxShadow: `0 0 16px ${t.accent}66` }} />
                  <span className="flex-1 min-w-0">
                    <span className="font-bold text-sm text-zinc-100 flex items-center gap-2">{t.name}
                      {t.official && <span className="font-mono text-[7px] uppercase tracking-[0.2em] text-sky-300 border border-sky-500/40 px-1.5 py-0.5">Official</span>}
                      {active && <span className="font-mono text-[7px] uppercase tracking-[0.2em] text-emerald-300 border border-emerald-500/40 px-1.5 py-0.5 flex items-center gap-1"><Check className="w-2.5 h-2.5" /> Active</span>}
                    </span>
                    <span className="font-mono text-[9px] text-zinc-500 block mt-0.5">{t.tagline || t.byline}</span>
                  </span>
                  {t.stripes.length > 0 && (
                    <span className="flex flex-col w-10 h-8 shrink-0 border border-white/10 overflow-hidden">
                      {t.stripes.map((c, i) => <span key={i} className="flex-1" style={{ backgroundColor: c }} />)}
                    </span>
                  )}
                </button>
              );
            })}
            <a href="/new-citizen/workshop?category=Config%20Pack" target="_blank" rel="noopener noreferrer" className="font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white transition-colors mt-1 underline underline-offset-4 w-fit">
              Get more themes on the workshop
            </a>

            {/* boot sequence + sound pack pickers (installed workshop packs) */}
            {[["boot_pack", "Boot Sequence"], ["sound_pack", "Sound Pack"]].map(([slot, label]) => {
              const packs = listInstalled(slot);
              if (!packs.length) return null;
              const active = activePack(slot);
              return (
                <div key={slot} className="border-t border-white/10 pt-3 mt-1" data-testid={`slot-${slot}`}>
                  <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-500 mb-2">{label}</p>
                  <div className="flex flex-col gap-1.5">
                    <button data-testid={`slot-${slot}-stock`} onClick={() => { setActivePack(slot, ""); onPacksChange?.(); audio.click(); }}
                      className={`text-left border px-3 py-2 font-mono text-[10px] transition-colors ${!active ? "border-white/50 text-zinc-100 bg-white/5" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                      Stock {label.toLowerCase()}
                    </button>
                    {packs.map((p) => (
                      <button key={p.id} data-testid={`slot-${slot}-${p.id.slice(0, 8)}`} onClick={() => { setActivePack(slot, p.id); setPackEnabled(p.id, true); onPacksChange?.(); audio.click(); toast.success(`${label}: ${p.title}`); }}
                        className={`text-left border px-3 py-2 font-mono text-[10px] transition-colors ${active?.id === p.id ? "border-white/50 text-zinc-100 bg-white/5" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                        {p.title} {p.official && <span className="text-sky-300 text-[8px] uppercase">· official</span>}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* my mods — enable/disable anything installed, even while keeping it downloaded */}
            {(() => {
              const all = listInstalled();
              const flat = Object.entries(all).flatMap(([slot, mods]) => mods.map((m) => ({ ...m, slot })));
              if (!flat.length) return null;
              return (
                <div className="border-t border-white/10 pt-3 mt-1" data-testid="my-mods">
                  <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-500 mb-2">My mods — downloaded packs</p>
                  <div className="flex flex-col gap-1.5">
                    {flat.map((m) => {
                      const on = isPackEnabled(m.id);
                      return (
                        <div key={m.id} data-testid={`mymod-${m.id.slice(0, 8)}`} className="flex items-center gap-2.5 border border-white/10 px-3 py-2">
                          <span className="flex-1 min-w-0 font-mono text-[10px] text-zinc-200 truncate">{m.title}
                            <span className="block text-[8px] uppercase text-zinc-600">{PACK_SLOTS[m.slot] || m.slot}</span></span>
                          <button data-testid={`mymod-toggle-${m.id.slice(0, 8)}`} onClick={() => { setPackEnabled(m.id, !on); onPacksChange?.(); audio.click(); }}
                            className={`font-mono text-[8px] uppercase tracking-[0.15em] border px-2.5 py-1 transition-colors ${on ? "border-emerald-500/50 text-emerald-300" : "border-white/15 text-zinc-500"}`}>
                            {on ? "On" : "Off"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
        {tab === "testing" && <TesterLinkPanel audio={audio} accent={accent} />}
        {tab === "language" && (
          <div data-testid="settings-language" className="flex flex-col gap-2">
            <p className="font-mono text-[9px] text-zinc-500 tracking-[0.15em] uppercase leading-relaxed">{t("settings.lang_note")}</p>
            {languages.map((l) => (
              <button key={l.id} data-testid={`lang-${l.id}`} onClick={() => { setLang(l.id); audio.click(); toast.success(`${t("settings.language")}: ${l.native}`); }}
                className={`text-left border px-4 py-3 flex items-center gap-3 transition-colors ${lang === l.id ? "border-white/60 bg-white/5" : "border-white/10 hover:border-white/30"}`}>
                <Globe className="w-4 h-4 shrink-0" style={{ color: lang === l.id ? accent : "#71717a" }} />
                <span className="flex-1 min-w-0">
                  <span className="font-bold text-sm text-zinc-100 block">{l.native}</span>
                  <span className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.15em]">{l.name}</span>
                </span>
                {lang === l.id && <span data-testid={`lang-active-${l.id}`} className="font-mono text-[7px] uppercase tracking-[0.2em] text-emerald-300 border border-emerald-500/40 px-1.5 py-0.5 flex items-center gap-1"><Check className="w-2.5 h-2.5" /> Active</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      {(tab === "graphics" || tab === "gameplay") && (
        <div className="mt-4 flex items-center gap-3">
          <button data-testid="settings-apply" disabled={!dirty} onClick={apply}
            className="px-6 py-2.5 font-mono text-[10px] tracking-[0.3em] uppercase text-black disabled:opacity-30 transition-opacity flex items-center gap-2" style={{ backgroundColor: accent }}>
            <Save className="w-3.5 h-3.5" /> {dirty ? t("settings.apply") : t("settings.applied")}
          </button>
          <p className="font-mono text-[9px] text-zinc-500 tracking-[0.15em] uppercase">mirrored into NCUser.ini by the native client</p>
        </div>
      )}
    </div>
  );
}

/* ---------------- CHARACTER / LOADOUT ---------------- */

const OUTFITS = ["Streetwear", "Tactical", "Business", "Biker", "Hi-Vis Tradie", "Off-Duty"];
const ACCENTS = ["#2E6BFF", "#ff8a2e", "#34d399", "#f43f5e", "#a78bfa", "#e4e4e7"];
const ACCESSORIES = ["None", "Cap", "Glasses", "Watch", "Chain", "Headset"];

export function CharacterPanel({ onBack, accent = "#2E6BFF", audio }) {
  const { t } = useI18n();
  const [me, setMe] = useState(undefined);
  const [loadouts, setLoadouts] = useState(() => load(STORED_LOADOUTS, [
    { name: "Everyday", outfit: "Streetwear", accent: "#2E6BFF", accessory: "Cap" },
  ]));
  const [activeIdx, setActiveIdx] = useState(0);
  const active = loadouts[activeIdx] || null;

  useEffect(() => {
    if (localStorage.getItem("nc_token")) api.get("/auth/me").then((r) => setMe(r.data)).catch(() => setMe(null));
    else setMe(null);
  }, []);

  const persist = (next) => { setLoadouts(next); localStorage.setItem(STORED_LOADOUTS, JSON.stringify(next)); };
  const patchActive = (p) => persist(loadouts.map((l, i) => (i === activeIdx ? { ...l, ...p } : l)));
  const addLoadout = () => {
    if (loadouts.length >= 4) { audio?.deny?.(); toast.error("4 loadout slots max"); return; }
    persist([...loadouts, { name: `Loadout ${loadouts.length + 1}`, outfit: "Streetwear", accent, accessory: "None" }]);
    setActiveIdx(loadouts.length);
    audio?.click?.();
  };
  const deleteLoadout = () => {
    if (loadouts.length <= 1) { audio?.deny?.(); return; }
    const next = loadouts.filter((_, i) => i !== activeIdx);
    persist(next); setActiveIdx(Math.max(0, activeIdx - 1)); audio?.back?.();
  };

  return (
    <div data-testid="character-panel" className="absolute inset-0 flex flex-col px-6 sm:px-10 pt-20 pb-6" style={{ animation: "ncFadeIn 0.3s ease both" }}>
      <div className="flex items-center gap-3">
        <button data-testid="character-back" onClick={onBack} className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-400 hover:text-zinc-100 transition-colors mr-2">‹ {t("settings.back")}</button>
        <h2 className="font-semibold uppercase tracking-[0.2em] text-lg sm:text-xl text-zinc-50">{t("nav.character")}</h2>
      </div>

      <div className="mt-5 flex-1 min-h-0 flex flex-col lg:flex-row gap-5 max-w-5xl">
        {/* identity */}
        <div className="w-full lg:w-72 shrink-0 flex flex-col gap-3">
          <div className="border border-white/10 bg-[#080a0f]/85 backdrop-blur-md p-5 flex flex-col gap-3">
            <p className="font-mono text-[9px] tracking-[0.35em] uppercase" style={{ color: accent }}>{t("char.identity")}</p>
            {me === undefined && <p className="font-mono text-[10px] text-zinc-400">loading…</p>}
            {me === null && IS_INGAME && (
              <p className="font-mono text-[11px] text-zinc-400 leading-relaxed">Sign-in lives on the web hub — visit <span className="text-zinc-200">specpixel.com</span> on your desktop and your Steam name & avatar will stamp your citizen card automatically.</p>
            )}
            {me === null && !IS_INGAME && (
              <div className="flex flex-col gap-2.5">
                <p className="font-mono text-[11px] text-zinc-300 leading-relaxed">Sign in to stamp your citizen card with your Steam name and avatar.</p>
                <button data-testid="character-steam-signin" onClick={() => { window.location.href = `/api/auth/steam/login?next=${encodeURIComponent("/cef/menu")}`; }}
                  className="py-2.5 font-bold uppercase tracking-[0.2em] text-xs text-black flex items-center justify-center gap-2" style={{ backgroundColor: "#66c0f4" }}>
                  <LogIn className="w-3.5 h-3.5" /> Sign in with Steam
                </button>
                <a href="/auth" className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400 hover:text-zinc-100 text-center transition-colors">or use a hub account</a>
              </div>
            )}
            {me && (
              <div className="flex items-center gap-3">
                {(me.steam_avatar || me.avatar_path)
                  ? <img src={me.steam_avatar || me.avatar_path} alt="" className="w-12 h-12 object-cover border border-white/15" />
                  : <div className="w-12 h-12 border border-white/15 flex items-center justify-center font-bold text-xl" style={{ color: accent }}>{(me.name || "C")[0].toUpperCase()}</div>}
                <div className="min-w-0">
                  <p data-testid="character-name" className="font-bold text-base text-zinc-100 truncate">{me.steam_persona || me.name || "Citizen"}</p>
                  <p className="font-mono text-[9px] text-zinc-400 uppercase tracking-[0.15em]">{me.steam_persona ? "Steam identity" : "Hub account"}</p>
                </div>
              </div>
            )}
            <p className="font-mono text-[9px] text-zinc-500 leading-relaxed border-t border-white/10 pt-3">
              Your citizen's body, face and record are created in the game client. Loadouts you build here travel with your menu profile.
            </p>
          </div>
        </div>

        {/* loadout builder */}
        <div className="flex-1 border border-white/10 bg-[#080a0f]/85 backdrop-blur-md p-5 flex flex-col gap-4 min-h-0 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-mono text-[9px] tracking-[0.35em] uppercase" style={{ color: accent }}>{t("char.loadouts")}</p>
            <span className="ml-auto" />
            {loadouts.map((l, i) => (
              <button key={i} data-testid={`loadout-tab-${i}`} onClick={() => { setActiveIdx(i); audio?.tick?.(); }}
                className={`px-3 py-1.5 border font-mono text-[10px] uppercase tracking-[0.12em] transition-colors ${i === activeIdx ? "text-black" : "border-white/15 text-zinc-300 hover:border-white/40"}`}
                style={i === activeIdx ? { backgroundColor: active?.accent || accent, borderColor: active?.accent || accent } : {}}>
                {l.name}
              </button>
            ))}
            <button data-testid="loadout-add" onClick={addLoadout} className="px-3 py-1.5 border border-dashed border-white/25 font-mono text-[10px] uppercase text-zinc-400 hover:border-white/50 transition-colors">{t("char.new")}</button>
          </div>

          {active && (
            <>
              {/* paper-doll */}
              <div className="flex items-center gap-6 flex-wrap">
                <div data-testid="loadout-doll" className="w-36 h-48 border flex flex-col items-center justify-center gap-1 relative" style={{ borderColor: `${active.accent}55`, background: `linear-gradient(180deg, ${active.accent}14 0%, transparent 60%)` }}>
                  <PersonStanding className="w-16 h-16" style={{ color: active.accent }} strokeWidth={1} />
                  <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-400">{active.outfit}</span>
                  <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500">{active.accessory !== "None" ? `+ ${active.accessory}` : "clean"}</span>
                  <span className="absolute top-2 left-2 w-2 h-2" style={{ backgroundColor: active.accent, boxShadow: `0 0 10px ${active.accent}` }} />
                </div>
                <div className="flex-1 min-w-60 flex flex-col gap-4">
                  <div>
                    <span className="font-mono text-[9px] tracking-[0.25em] text-zinc-400 uppercase block mb-2">{t("char.name")}</span>
                    <input data-testid="loadout-name" value={active.name} maxLength={24} onChange={(e) => patchActive({ name: e.target.value })}
                      className="bg-black border border-white/15 px-3 py-2 font-mono text-[12px] text-zinc-100 focus:outline-none focus:border-white/40 w-full max-w-64" />
                  </div>
                  <div>
                    <span className="font-mono text-[9px] tracking-[0.25em] text-zinc-400 uppercase block mb-2 flex items-center gap-1.5"><Shirt className="w-3.5 h-3.5" /> {t("char.outfit")}</span>
                    <div className="flex gap-1.5 flex-wrap">
                      {OUTFITS.map((o) => (
                        <button key={o} data-testid={`outfit-${o.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => { patchActive({ outfit: o }); audio?.tick?.(); }}
                          className={`px-3 py-1.5 border font-mono text-[10px] uppercase tracking-[0.1em] transition-colors ${active.outfit === o ? "text-black" : "border-white/15 text-zinc-300 hover:border-white/40"}`}
                          style={active.outfit === o ? { backgroundColor: active.accent, borderColor: active.accent } : {}}>
                          {o}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="font-mono text-[9px] tracking-[0.25em] text-zinc-400 uppercase block mb-2">{t("char.accent")}</span>
                    <div className="flex gap-2">
                      {ACCENTS.map((c) => (
                        <button key={c} data-testid={`accent-${c.slice(1)}`} onClick={() => { patchActive({ accent: c }); audio?.tick?.(); }}
                          className="w-8 h-8 border-2 transition-transform hover:scale-110" style={{ backgroundColor: c, borderColor: active.accent === c ? "#fff" : "transparent" }} />
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="font-mono text-[9px] tracking-[0.25em] text-zinc-400 uppercase block mb-2">{t("char.accessory")}</span>
                    <div className="flex gap-1.5 flex-wrap">
                      {ACCESSORIES.map((a) => (
                        <button key={a} data-testid={`acc-${a.toLowerCase()}`} onClick={() => { patchActive({ accessory: a }); audio?.tick?.(); }}
                          className={`px-3 py-1.5 border font-mono text-[10px] uppercase tracking-[0.1em] transition-colors ${active.accessory === a ? "text-black" : "border-white/15 text-zinc-300 hover:border-white/40"}`}
                          style={active.accessory === a ? { backgroundColor: active.accent, borderColor: active.accent } : {}}>
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 border-t border-white/10 pt-4">
                <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.15em] flex-1">Saved automatically to this menu profile · the game client applies your active loadout on join</p>
                <button data-testid="loadout-delete" onClick={deleteLoadout} className="flex items-center gap-1.5 border border-red-500/40 text-red-400 px-3 py-2 font-mono text-[9px] uppercase tracking-[0.2em] hover:bg-red-500/10 transition-colors">
                  <Trash2 className="w-3 h-3" /> {t("char.delete")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- CREDITS ---------------- */

const CREDITS = [
  ["A Specpixel Entertainment World", ""],
  ["Game Director", "Specpixel Studio"],
  ["Unreal Engine 5", "Epic Games"],
  ["New Citizen Hub", "Server browser · Owner deck · NCDB"],
  ["In-Game Surfaces", "Loading · Phone · CAD · Courthouse · Menu"],
  ["Bridge Engineering", "nc_bridge.py — world sync daemon"],
  ["AI Systems", "911 dispatch · Witness personalities"],
  ["", ""],
  ["Thank you", "Every citizen keeps the city alive"],
];

export function CreditsPanel({ onBack, accent = "#2E6BFF" }) {
  const { t } = useI18n();
  return (
    <div data-testid="credits-panel" className="absolute inset-0 flex flex-col px-6 sm:px-10 pt-20 overflow-hidden" style={{ animation: "ncFadeIn 0.3s ease both" }}>
      <div className="flex items-center gap-3">
        <button data-testid="credits-back" onClick={onBack} className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-400 hover:text-zinc-100 transition-colors mr-2">‹ {t("settings.back")}</button>
        <h2 className="font-semibold uppercase tracking-[0.2em] text-lg sm:text-xl text-zinc-50">{t("nav.credits")}</h2>
      </div>
      <div className="flex-1 relative overflow-hidden mt-4 border border-white/10 bg-black/30">
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#080a0f] to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#080a0f] to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-x-0 top-0 text-center" style={{ animation: "ncCredits 20s linear infinite" }}>
          {CREDITS.map(([role, name], i) => (
            <div key={i} className="mb-9">
              <p className="font-mono text-[11px] tracking-[0.45em] uppercase mb-1.5" style={{ color: accent }}>{role}</p>
              {name && <p className="font-medium uppercase tracking-[0.15em] text-zinc-100 text-lg">{name}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
